<template>
  <div
    class="terms-of-use-pdf-viewer"
    :class="{ 'terms-of-use-pdf-viewer--fullscreen': isFullscreen }"
  >
    <div class="terms-of-use-pdf-viewer__toolbar">
      <div
        v-if="!error && totalPages > 0"
        class="terms-of-use-pdf-viewer__nav-group"
      >
        <span class="terms-of-use-pdf-viewer__nav-label">
          {{ $t('pagina') }}
        </span>
        <b-form-select
          v-model="currentPage"
          :options="pageOptions"
          :title="$t('pagina')"
          :aria-label="$t('pagina')"
          class="terms-of-use-pdf-viewer__page-select"
          @change="scrollToPage"
        />
      </div>
      <div
        v-if="isFullscreen && !error && totalPages > 0"
        class="terms-of-use-pdf-viewer__zoom-group"
      >
        <b-button
          variant="light"
          size="sm"
          :disabled="zoomFactor <= minZoom"
          :aria-label="$t('diminuirzoom')"
          @click="zoomOut"
        >
          -
        </b-button>
        <span class="terms-of-use-pdf-viewer__zoom-level">
          {{ zoomPercentage }}%
        </span>
        <b-button
          variant="light"
          size="sm"
          :disabled="zoomFactor >= maxZoom"
          :aria-label="$t('aumentarzoom')"
          @click="zoomIn"
        >
          +
        </b-button>
      </div>
      <b-button
        variant="none"
        class="terms-of-use-pdf-viewer__fullscreen-btn"
        :title="isFullscreen ? $t('sairtelacheia') : $t('telacheia')"
        @click="toggleFullscreen"
      >
        <svg-icon
          :icon-class="isFullscreen ? 'exit-fullscreen' : 'fullscreen'"
        />
      </b-button>
    </div>
    <div ref="scrollContainer" class="terms-of-use-pdf-viewer__document">
      <div v-if="error" class="terms-of-use-pdf-viewer__error">
        {{ $t('erroaocarregarpdf') }}
      </div>
      <template v-else>
        <figure
          v-for="page in pages"
          :key="page.number"
          ref="pageEls"
          :data-page="page.number"
          class="terms-of-use-pdf-viewer__page"
          :class="{
            'terms-of-use-pdf-viewer__page--current':
              page.number === currentPage
          }"
          :style="pageStyle(page)"
          :aria-label="pageAriaLabel(page)"
        >
          <canvas class="terms-of-use-pdf-viewer__canvas" />
        </figure>
      </template>
    </div>
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf'
import 'pdfjs-dist/legacy/build/pdf.worker.entry'

const RENDER_ROOT_MARGIN = '600px 0px'
const SCROLL_SETTLE_DELAY = 150
const RESIZE_DEBOUNCE_DELAY = 150
const PAGE_BORDER = 2
const MAX_CANVAS_PIXELS = 16777216

export default {
  name: 'TermsOfUsePdfViewer',
  props: {
    file: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      error: false,
      pages: [],
      totalPages: 0,
      currentPage: 1,
      baseScale: 1,
      zoomFactor: 1,
      minZoom: 0.5,
      maxZoom: 2.5,
      zoomStep: 0.25,
      isFullscreen: false
    }
  },
  computed: {
    effectiveScale() {
      return this.baseScale * this.zoomFactor
    },
    zoomPercentage() {
      return Math.round(this.zoomFactor * 100)
    },
    pageOptions() {
      const options = []
      for (let page = 1; page <= this.totalPages; page += 1) {
        options.push({ value: page, text: `${page} / ${this.totalPages}` })
      }
      return options
    }
  },
  watch: {
    effectiveScale() {
      this.rerenderPagesInRange()
    }
  },
  created() {
    this.pdfDocument = null
    this.renderTasks = {}
    this.renderedScales = {}
    this.visibleHeights = {}
    this.pagesInRenderRange = new Set()
    this.renderObserver = null
    this.visibilityObserver = null
    this.resizeObserver = null
    this.programmaticScrollTarget = null
    this.scrollSettleTimer = null
    this.resizeDebounceTimer = null
  },
  mounted() {
    this.loadPdf()
  },
  beforeDestroy() {
    this.teardownObservers()
    this.cancelAllRenders()
    clearTimeout(this.scrollSettleTimer)
    clearTimeout(this.resizeDebounceTimer)
    const container = this.$refs.scrollContainer
    if (container) container.removeEventListener('scroll', this.onScroll)
    if (this.pdfDocument) {
      this.pdfDocument.destroy()
      this.pdfDocument = null
    }
  },
  methods: {
    async loadPdf() {
      this.error = false
      try {
        this.pdfDocument = await pdfjsLib.getDocument(this.file).promise
        this.totalPages = this.pdfDocument.numPages
        this.currentPage = 1

        const pageNumbers = Array.from(
          { length: this.totalPages },
          (_, index) => index + 1
        )
        const pdfPages = await Promise.all(
          pageNumbers.map((number) => this.pdfDocument.getPage(number))
        )
        this.pages = pdfPages.map((pdfPage) => {
          const viewport = pdfPage.getViewport({ scale: 1 })
          return {
            number: pdfPage.pageNumber,
            width: viewport.width,
            height: viewport.height
          }
        })

        this.computeBaseScale()
        await this.$nextTick()
        this.setupObservers()
      } catch (error) {
        this.error = true
      }
    },
    pageStyle(page) {
      return {
        width: `${Math.floor(page.width * this.effectiveScale)}px`,
        height: `${Math.floor(page.height * this.effectiveScale)}px`
      }
    },
    pageAriaLabel(page) {
      return `${this.$t('termosdeuso')} - ${this.$t('pagina')} ${page.number}`
    },
    getPageElement(pageNumber) {
      const elements = this.$refs.pageEls || []
      return (
        elements.find(
          (element) => Number(element.dataset.page) === pageNumber
        ) || null
      )
    },
    getContainerPadding(container) {
      const style = window.getComputedStyle(container)
      return {
        top: parseFloat(style.paddingTop) || 0,
        left: parseFloat(style.paddingLeft) || 0,
        right: parseFloat(style.paddingRight) || 0
      }
    },
    computeBaseScale() {
      const container = this.$refs.scrollContainer
      if (!container || !this.pages.length) return

      const padding = this.getContainerPadding(container)
      const availableWidth =
        container.clientWidth - padding.left - padding.right - PAGE_BORDER * 2
      if (availableWidth <= 0) return

      const widestPage = Math.max(...this.pages.map((page) => page.width))
      if (!widestPage) return

      this.baseScale = availableWidth / widestPage
    },
    setupObservers() {
      const container = this.$refs.scrollContainer
      if (!container) return

      container.addEventListener('scroll', this.onScroll, { passive: true })

      if (typeof IntersectionObserver === 'undefined') {
        this.pages.forEach((page) => this.renderPage(page.number))
      } else {
        this.renderObserver = new IntersectionObserver(
          this.onRenderIntersection,
          { root: container, rootMargin: RENDER_ROOT_MARGIN }
        )
        this.visibilityObserver = new IntersectionObserver(
          this.onVisibilityIntersection,
          { root: container, threshold: [0, 0.25, 0.5, 0.75, 1] }
        )
        this.pages.forEach((page) => {
          const element = this.getPageElement(page.number)
          if (!element) return
          this.renderObserver.observe(element)
          this.visibilityObserver.observe(element)
        })
      }

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(this.onContainerResize)
        this.resizeObserver.observe(container)
      }
    },
    teardownObservers() {
      if (this.renderObserver) this.renderObserver.disconnect()
      if (this.visibilityObserver) this.visibilityObserver.disconnect()
      if (this.resizeObserver) this.resizeObserver.disconnect()
      this.renderObserver = null
      this.visibilityObserver = null
      this.resizeObserver = null
    },
    onContainerResize() {
      clearTimeout(this.resizeDebounceTimer)
      this.resizeDebounceTimer = setTimeout(
        this.computeBaseScale,
        RESIZE_DEBOUNCE_DELAY
      )
    },
    onRenderIntersection(entries) {
      entries.forEach((entry) => {
        const pageNumber = Number(entry.target.dataset.page)
        if (entry.isIntersecting) {
          this.pagesInRenderRange.add(pageNumber)
          this.renderPage(pageNumber)
        } else {
          this.pagesInRenderRange.delete(pageNumber)
          this.cancelRender(pageNumber)
        }
      })
    },
    onVisibilityIntersection(entries) {
      entries.forEach((entry) => {
        const pageNumber = Number(entry.target.dataset.page)
        this.visibleHeights[pageNumber] = entry.isIntersecting
          ? entry.intersectionRect.height
          : 0
      })
      if (this.programmaticScrollTarget !== null) return
      this.updateCurrentPageFromVisibility()
    },
    updateCurrentPageFromVisibility() {
      let bestPage = this.currentPage
      let bestHeight = 0
      Object.keys(this.visibleHeights).forEach((key) => {
        const height = this.visibleHeights[key]
        if (height > bestHeight) {
          bestHeight = height
          bestPage = Number(key)
        }
      })
      if (bestHeight > 0) this.currentPage = bestPage
    },
    onScroll() {
      if (this.programmaticScrollTarget === null) return
      this.armScrollSettleTimer()
    },
    armScrollSettleTimer() {
      clearTimeout(this.scrollSettleTimer)
      this.scrollSettleTimer = setTimeout(() => {
        this.programmaticScrollTarget = null
      }, SCROLL_SETTLE_DELAY)
    },
    scrollToPage(pageNumber) {
      const container = this.$refs.scrollContainer
      const pageElement = this.getPageElement(pageNumber)
      if (!container || !pageElement) return

      this.programmaticScrollTarget = pageNumber
      this.currentPage = pageNumber
      this.armScrollSettleTimer()
      container.scrollTo({
        top: pageElement.offsetTop - this.getContainerPadding(container).top,
        behavior: 'smooth'
      })
    },
    getPixelRatio(viewport) {
      const deviceRatio = window.devicePixelRatio || 1
      const maxRatio = Math.sqrt(
        MAX_CANVAS_PIXELS / (viewport.width * viewport.height)
      )
      return Math.min(deviceRatio, maxRatio)
    },
    shouldSkipRender(pageNumber, scale) {
      if (this.renderedScales[pageNumber] === scale) return true

      const inFlight = this.renderTasks[pageNumber]
      if (!inFlight) return false
      if (inFlight.scale === scale) return true

      this.cancelRender(pageNumber)
      return false
    },
    async renderPage(pageNumber) {
      if (!this.pdfDocument) return

      const scale = this.effectiveScale
      if (this.shouldSkipRender(pageNumber, scale)) return

      const pageElement = this.getPageElement(pageNumber)
      const canvas = pageElement && pageElement.querySelector('canvas')
      if (!canvas) return

      try {
        const pdfPage = await this.pdfDocument.getPage(pageNumber)
        if (this.effectiveScale !== scale) return
        if (this.shouldSkipRender(pageNumber, scale)) return

        const viewport = pdfPage.getViewport({ scale })
        const pixelRatio = this.getPixelRatio(viewport)
        canvas.width = Math.floor(viewport.width * pixelRatio)
        canvas.height = Math.floor(viewport.height * pixelRatio)

        const renderParams = {
          canvasContext: canvas.getContext('2d'),
          viewport
        }
        if (pixelRatio !== 1) {
          renderParams.transform = [pixelRatio, 0, 0, pixelRatio, 0, 0]
        }

        const task = pdfPage.render(renderParams)
        this.renderTasks[pageNumber] = { task, scale }
        await task.promise
        this.renderedScales[pageNumber] = scale
      } catch (error) {
        if (!error || error.name !== 'RenderingCancelledException') {
          this.error = true
        }
      } finally {
        const current = this.renderTasks[pageNumber]
        if (current && current.scale === scale) {
          delete this.renderTasks[pageNumber]
        }
      }
    },
    cancelRender(pageNumber) {
      const entry = this.renderTasks[pageNumber]
      if (!entry) return
      entry.task.cancel()
      delete this.renderTasks[pageNumber]
    },
    cancelAllRenders() {
      Object.keys(this.renderTasks).forEach((key) =>
        this.cancelRender(Number(key))
      )
    },
    rerenderPagesInRange() {
      this.pagesInRenderRange.forEach((pageNumber) =>
        this.renderPage(pageNumber)
      )
    },
    zoomIn() {
      if (this.zoomFactor >= this.maxZoom) return
      this.zoomFactor = Math.min(
        this.maxZoom,
        +(this.zoomFactor + this.zoomStep).toFixed(2)
      )
    },
    zoomOut() {
      if (this.zoomFactor <= this.minZoom) return
      this.zoomFactor = Math.max(
        this.minZoom,
        +(this.zoomFactor - this.zoomStep).toFixed(2)
      )
    },
    toggleFullscreen(event) {
      // Remove o foco apos a interacao (padrao do Design System - o botao
      // nao deve permanecer com o estado de foco destacado apos o clique).
      if (event && event.currentTarget) event.currentTarget.blur()
      this.isFullscreen = !this.isFullscreen
      if (!this.isFullscreen) this.zoomFactor = 1
      if (typeof ResizeObserver === 'undefined') {
        this.$nextTick(this.computeBaseScale)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.terms-of-use-pdf-viewer {
  display: flex;
  flex-direction: column;
  width: 100%;
  font-family: 'Poppins', sans-serif;
  color: #495057;

  &--fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1055;
    background: #fff;
    padding: 20px;
  }

  &--fullscreen &__document {
    flex: 1;
    min-height: 0;
    max-height: none;
  }

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 12px;
    padding: 12px 16px;
    background: #f5f5f5;
    border: 1px solid #e0e0e0;
    border-radius: 8px 8px 0 0;
  }

  &__nav-group,
  &__zoom-group {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__nav-label {
    font-size: 14px;
  }

  &__page-select {
    width: 130px;
    font-family: inherit;

    // Foco verde (mesma cor das linhas finas que delimitam as paginas),
    // no lugar do azul padrao do Bootstrap (#80bdff / rgba(0,123,255,.25)).
    &:focus {
      border-color: rgba(74, 127, 52, 0.35);
      box-shadow: 0 0 0 0.2rem rgba(74, 127, 52, 0.35);
    }
  }

  &__zoom-level {
    min-width: 50px;
    text-align: center;
    font-size: 14px;
  }

  &__fullscreen-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    padding: 0;
    background: #fff;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    color: #495057;
    font-size: 20px;

    &:hover {
      background: #fafafa;
    }

    // Remove o anel azul padrao do Bootstrap (.btn:focus) ao clicar para
    // maximizar/minimizar. Feito via CSS (nao so JS/blur) para nao depender
    // de timing: o navegador foca no mousedown, antes do handler de click
    // rodar, entao so blur() no JS deixava um flash azul visivel.
    &:focus,
    &.focus {
      outline: 0;
      box-shadow: none;
    }
  }

  &__document {
    position: relative;
    width: 100%;
    max-height: min(450px, calc(100vh - 330px));
    min-height: 180px;
    padding: 16px;
    overflow-y: scroll;
    overflow-x: auto;
    background: #f5f5f5;
    border: 1px solid #e0e0e0;
    border-top: none;
    border-radius: 0 0 8px 8px;
  }

  &__page {
    position: relative;
    box-sizing: content-box;
    margin: 0 auto 16px;
    background: #fff;
    border: 2px solid transparent;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);

    // Borda visivel apenas na pagina atual (mesmo controle usado pelo
    // seletor de pagina no toolbar). Borda sempre reservada (transparent)
    // para nao deslocar o layout ao trocar de pagina atual.
    &--current {
      border-color: rgba(74, 127, 52, 0.35);
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__canvas {
    display: block;
    width: 100%;
    height: 100%;
  }

  &__error {
    color: #a94442;
    text-align: center;
    padding: 20px;
  }

  @media (max-width: 575.98px) {
    &--fullscreen {
      padding: 8px;
    }

    &__toolbar {
      gap: 8px;
      padding: 8px 12px;
    }

    &__nav-label {
      display: none;
    }

    &__page-select {
      width: 100px;
    }

    &__zoom-level {
      min-width: 44px;
    }

    &__document {
      max-height: min(450px, calc(100vh - 360px));
      padding: 12px;
    }

    &__page {
      margin-bottom: 12px;
    }
  }
}
</style>
